import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, Loader2, UserPlus, ArrowRight } from "lucide-react";
import { usePublicLang, LangToggle } from "@/hooks/usePublicLang";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_OPTIONS = [
  "Business Cards", "Flyers", "Sticker", "Brochure", "Menu", "Banner", "Poster", "Otros"
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming","District of Columbia",
  "Puerto Rico","Guam","U.S. Virgin Islands","American Samoa","Northern Mariana Islands"
];

const ClientRegisterPage = () => {
  const { lang, setLang, t } = usePublicLang();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", street_address: "", unit: "", city: "", state: "", zip_code: "", service_types: []
  });

  const toggleService = (service) => {
    setForm(prev => ({
      ...prev,
      service_types: prev.service_types.includes(service) ? prev.service_types.filter(s => s !== service) : [...prev.service_types, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error(lang === "es" ? "Nombre y email son obligatorios" : "Name and email are required"); return; }
    setLoading(true);
    try {
      const payload = { ...form, city_state: [form.city, form.state].filter(Boolean).join(", ") };
      await axios.post(`${API}/api/public/register-client`, payload);
      setSubmitted(true);
    } catch (error) { toast.error(error.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const langToggle = <div className="fixed top-4 right-4 z-50"><LangToggle lang={lang} setLang={setLang} /></div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        {langToggle}
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-200">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-4">{t("register_success_title")}</h1>
          <p className="text-stone-600 mb-4 text-lg">{t("register_success_sub")}</p>
          <p className="text-stone-500 mb-8">{t("register_success_info")}</p>
          <Button onClick={() => window.location.href = "/"} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white h-12 px-8 rounded-xl">
            {t("register_back")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const inputCls = "bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-emerald-500";

  return (
    <div className="min-h-screen bg-stone-50 py-16 px-4" data-testid="client-register-page">
      {langToggle}
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-10">
          <img src="/assets/logo-sally-dark.png" alt="Sally Better" className="h-16 mx-auto mb-6" onError={(e) => e.target.style.display = 'none'} />
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5 border border-emerald-200">
            <UserPlus className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-3">{t("register_title")}</h1>
          <p className="text-stone-600">{t("register_subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-700">{t("register_name")}</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required
                  className={inputCls} data-testid="register-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700">{t("register_email")}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} required
                  className={inputCls} data-testid="register-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700">{t("register_phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                className={inputCls} data-testid="register-phone" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700">{t("register_street")}</Label>
              <Input value={form.street_address} onChange={(e) => setForm(p => ({ ...p, street_address: e.target.value }))}
                className={inputCls} data-testid="register-street" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-700">{t("register_unit")}</Label>
                <Input value={form.unit} onChange={(e) => setForm(p => ({ ...p, unit: e.target.value }))}
                  className={inputCls} data-testid="register-unit" />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700">{t("register_city")}</Label>
                <Input value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))}
                  className={inputCls} data-testid="register-city" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-700">{t("register_state")}</Label>
                <Select value={form.state} onValueChange={(v) => setForm(p => ({ ...p, state: v }))}>
                  <SelectTrigger className="bg-white border-stone-300 text-stone-900" data-testid="register-state">
                    <SelectValue placeholder="..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-stone-200 max-h-60">
                    {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700">{t("register_zip")}</Label>
                <Input value={form.zip_code} onChange={(e) => setForm(p => ({ ...p, zip_code: e.target.value }))} maxLength={10}
                  className={inputCls} data-testid="register-zip" />
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-stone-700">{t("register_services")}</Label>
              <div className="grid grid-cols-2 gap-2">
                {SERVICE_OPTIONS.map(service => (
                  <label key={service} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${form.service_types.includes(service) ? "border-emerald-600 bg-emerald-50" : "border-stone-300 hover:border-stone-400"}`} data-testid={`register-service-${service}`}>
                    <Checkbox checked={form.service_types.includes(service)} onCheckedChange={() => toggleService(service)} />
                    <span className={`text-sm ${form.service_types.includes(service) ? "text-emerald-700 font-medium" : "text-stone-700"}`}>{service}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-14 text-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold" data-testid="register-submit-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("register_submit")}
          </Button>
          <p className="text-center text-sm text-stone-500 pb-4">{t("register_consent")}</p>
        </form>
      </div>
    </div>
  );
};

export default ClientRegisterPage;
